// Centralized layout & shell styles for GameBoardControl
// Keep this file free of game logic and animations.

import { SxProps, Theme } from "@mui/material/styles";

// Wrapper when only the board is shown
export const boardOnlyWrapperSx = (sx?: SxProps<Theme>): SxProps<Theme> => ({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  ...(sx || {}),
});

// Card around the board & header
export const gameCardSx = (
  themeConfig: any,
  sx?: SxProps<Theme>
): SxProps<Theme> => ({
  mx: { xs: 0, sm: 2, md: 3 },
  mt: { xs: 1.5, sm: 3 },
  mb: { xs: 1.5, sm: 2 },
  // Mobile: Block ohne Rundungen, Desktop: klassischer Card-Look
  borderRadius: { xs: 0, sm: 3 },
  maxWidth: 700,
  width: "100%",
  background: themeConfig.dialog.background,
  border: themeConfig.dialog.border,
  ...(sx || {}),
});

// Generic CardContent layout (column, centered)
// Padding wird von den einzelnen Sektionen (Header, Board) selbst gesteuert,
// damit der Header oben bündig mit der Card beginnt – wie bei „Verbleibende Spielsteine“.
export const cardContentSx: SxProps<Theme> = {
  p: 0,
  "&:last-child": { pb: 2 },
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
};

// Header layout (current player, title, current stone)
export const headerContainerSx: SxProps<Theme> = {
  width: "100%",
  mb: { xs: 2, sm: 3 },
  mt: { xs: 0.5, sm: -1 },
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  justifyContent: "center",
  gap: { xs: 1, sm: 1.5 },
  // Einheitliche Sektion wie bei „Verbleibende Spielsteine“
  px: { xs: 2, sm: 3 },
  py: { xs: 1.5, sm: 2 },
  borderRadius: { xs: 0, sm: 0 },
  bgcolor: "action.hover",
  borderBottom: "2px solid",
  borderColor: "divider",
};

export const playerInfoContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-end",
  gap: 0.25,
  minWidth: 0,
  flex: 1,
};

export const playerNameSx: SxProps<Theme> = {
  color: "text.primary",
  fontSize: "0.9rem",
  lineHeight: 1.2,
};

export const playerSubtitleSx: SxProps<Theme> = {
  color: "text.secondary",
  fontSize: "0.75rem",
  lineHeight: 1,
};

export const headerTitleContainerSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1.5,
  justifyContent: "center",
  flex: 1,
};

export const headerTitleSx: SxProps<Theme> = {
  color: "primary.main",
  fontSize: { xs: "1.3rem", sm: "1.5rem" },
  textAlign: "center",
};

export const infoButtonSx: SxProps<Theme> = {
  color: "primary.main",
  width: 32,
  height: 32,
  "&:hover": {
    bgcolor: "action.hover",
  },
};

// Current stone box on the right side of header
export const currentStoneContainerSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: { xs: "row", sm: "column" },
  alignItems: { xs: "center", sm: "flex-end" },
  justifyContent: { xs: "space-between", sm: "flex-start" },
  gap: 0.5,
  flex: 1,
};

export const currentStoneBoxSx = (themeConfig: any): SxProps<Theme> => ({
  bgcolor: themeConfig.tile.background,
  width: { xs: 52, sm: 60 },
  height: { xs: 52, sm: 60 },
  borderRadius: 1.5,
  border: {
    xs: "none",
    sm: themeConfig.tile.border,
  },
  boxShadow: {
    xs: "none",
    sm: themeConfig.tile.boxShadow,
  },
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  transition: "all 0.2s",
  "&:hover": {
    transform: {
      xs: "none",
      sm: "scale(1.05)",
    },
    boxShadow: {
      xs: "none",
      sm: themeConfig.tile.hoverBoxShadow,
    },
  },
});

export const currentStoneLabelSx: SxProps<Theme> = {
  color: "text.secondary",
  fontSize: "0.7rem",
  fontWeight: 500,
};

// Settings dialog shell
export const dialogPaperSx = (themeConfig: any): SxProps<Theme> => ({
  borderRadius: 3,
  background: themeConfig.dialog.background,
  border: themeConfig.dialog.border,
});

export const dialogTitleSx = (themeConfig: any): SxProps<Theme> => ({
  borderBottom: themeConfig.dialog.headerBorder,
  background: themeConfig.dialog.headerBackground,
  pb: 2,
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
});

export const dialogCloseButtonSx: SxProps<Theme> = {
  color: "text.secondary",
  "&:hover": {
    color: "error.main",
    bgcolor: "action.hover",
  },
};

export const dialogContentSx: SxProps<Theme> = { p: 3 };
